import React from 'react';
import { cn } from '@/lib/utils';

const AdminDashboardSkeleton: React.FC = () => {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header & Controls - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="h-8 w-48 bg-dark-300 rounded-lg mb-2"></div>
          <div className="h-4 w-72 bg-dark-300 rounded-lg"></div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative w-full sm:w-auto">
            <div className="h-10 w-64 bg-dark-300 rounded-lg"></div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0">
            <div className="h-10 w-24 bg-dark-300 rounded-lg"></div>
            <div className="h-10 w-24 bg-dark-300 rounded-lg"></div>
          </div>
        </div>
      </div>

      {/* Time Range Filter */}
      <div className="bg-dark-200 rounded-xl p-4 border border-dark-300">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="h-4 w-24 bg-dark-300 rounded-lg"></div>
          <div className="flex flex-wrap items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 w-16 bg-dark-300 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-dark-200 rounded-xl p-6 border border-dark-300">
            <div className="flex items-start justify-between">
              <div>
                <div className="h-6 w-32 bg-dark-300 rounded-lg mb-4"></div>
                <div className="h-8 w-20 bg-dark-300 rounded-lg mb-2"></div>
                <div className="h-4 w-24 bg-dark-300 rounded-lg"></div>
              </div>
              <div className="h-10 w-10 bg-dark-300 rounded-lg"></div>
            </div>
            {i % 2 === 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="h-4 w-24 bg-dark-300 rounded-lg"></div>
                  <div className="h-4 w-12 bg-dark-300 rounded-lg"></div>
                </div>
                <div className="h-1.5 w-full bg-dark-300 rounded-full"></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Charts & Recent Users */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* User Growth */}
        <div className="bg-dark-200 rounded-xl p-6 border border-dark-300 md:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 w-32 bg-dark-300 rounded-lg"></div>
            <div className="h-8 w-28 bg-dark-300 rounded-lg hidden sm:block"></div>
          </div>
          <div className="h-64 bg-dark-300 rounded-lg"></div>
        </div>

        {/* Top Cities */}
        <div className="bg-dark-200 rounded-xl p-6 border border-dark-300 md:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 w-24 bg-dark-300 rounded-lg"></div>
            <div className="h-8 w-20 bg-dark-300 rounded-lg"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 bg-dark-300 rounded-lg"></div>
                  <div className="h-4 w-32 bg-dark-300 rounded-lg"></div>
                </div>
                <div className="h-4 w-16 bg-dark-300 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Clubs */}
        <div className="bg-dark-200 rounded-xl p-6 border border-dark-300 md:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 w-24 bg-dark-300 rounded-lg"></div>
            <div className="h-8 w-20 bg-dark-300 rounded-lg hidden sm:block"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 bg-dark-300 rounded-lg"></div>
                  <div className="h-4 w-32 bg-dark-300 rounded-lg"></div>
                </div>
                <div className="h-4 w-16 bg-dark-300 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Sports */}
        <div className="bg-dark-200 rounded-xl p-6 border border-dark-300 md:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 w-32 bg-dark-300 rounded-lg"></div>
            <div className="h-8 w-20 bg-dark-300 rounded-lg"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <div className="h-4 w-32 bg-dark-300 rounded-lg"></div>
                  <div className="h-4 w-16 bg-dark-300 rounded-lg"></div>
                </div>
                <div className="h-1.5 w-full bg-dark-300 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Users Table */}
      <div className="bg-dark-200 rounded-xl p-6 border border-dark-300">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 w-32 bg-dark-300 rounded-lg"></div>
          <div className="h-8 w-28 bg-dark-300 rounded-lg hidden sm:block"></div>
        </div>
        <div className="overflow-x-auto -mx-6 px-6">
          {/* Desktop Table - Hidden on mobile */}
          <div className="hidden md:block">
            <div className="w-full">
              <div className="border-b border-dark-300 pb-3">
                <div className="grid grid-cols-6 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-4 w-full bg-dark-300 rounded-lg"></div>
                  ))}
                </div>
              </div>
              <div className="space-y-4 pt-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="grid grid-cols-6 gap-4 border-b border-dark-300 pb-3">
                    {[1, 2, 3, 4, 5, 6].map((j) => (
                      <div key={j} className="h-4 w-full bg-dark-300 rounded-lg"></div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Mobile Cards - Shown only on mobile */}
          <div className="md:hidden space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-dark-300 rounded-lg p-4 border border-dark-400">
                <div className="flex justify-between items-start mb-2">
                  <div className="space-y-2">
                    <div className="h-5 w-32 bg-dark-400 rounded-lg"></div>
                    <div className="h-4 w-24 bg-dark-400 rounded-lg"></div>
                  </div>
                  <div className="h-6 w-16 bg-dark-400 rounded-full"></div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <div className="h-4 w-28 bg-dark-400 rounded-lg"></div>
                    <div className="h-4 w-32 bg-dark-400 rounded-lg"></div>
                  </div>
                  <div className="h-8 w-16 bg-dark-400 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-dark-200 rounded-xl p-6 border border-dark-300 overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 w-32 bg-dark-300 rounded-lg"></div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="h-4 w-40 bg-dark-300 rounded-lg"></div>
            <div className="h-4 w-32 bg-dark-300 rounded-lg"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-dark-300 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="h-4 w-20 bg-dark-400 rounded-lg"></div>
                <div className="h-4 w-12 bg-dark-400 rounded-lg"></div>
              </div>
              <div className="h-1.5 w-full bg-dark-400 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardSkeleton;